import { Injectable } from '@angular/core';
import * as FileSaver from 'file-saver';
import * as XLSX from 'xlsx';

const EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
const EXCEL_EXTENSION = '.xlsx';

@Injectable({
  providedIn: 'root'
})

export class ServiceExportExcel {

  public exportAsExcelFile(json: any[], excelFileName: string): void {
    const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(json);
    this.applyDateFormat(worksheet);
    const workbook: XLSX.WorkBook = { Sheets: { 'data': worksheet }, SheetNames: ['data'] };
    const excelBuffer: any = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    this.saveAsExcelFile(excelBuffer, excelFileName);
  }

  /** Same as exportAsExcelFile, but keeps selected OTA/OTD time columns as prepared text. */
  public exportAsExcelFileForOtaOtd(json: any[], excelFileName: string): void {
    const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(json);
    this.applyDateFormat(worksheet, false, [
      'Vehicle_reporting_time',
      'Start_time',
      'Stop_time',
    ]);
    const workbook: XLSX.WorkBook = { Sheets: { 'data': worksheet }, SheetNames: ['data'] };
    const excelBuffer: any = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    this.saveAsExcelFile(excelBuffer, excelFileName);
  }

  public exportAsExcelFileForTrips(
    json: any[],
    excelFileName: string,
    isAllianz = false,
    numericColumns: string[] = [],
    excludeTimeColumns: string[] = [],
  ): void {
    const jsonKeys = Object.keys(json[0]);
    let objectMaxLength = [];
    for (let i = 0; i < json.length; i++) {
      let value = json[i];
      for (let j = 0; j < jsonKeys.length; j++) {
        if (typeof value[jsonKeys[j]] == "number") {
          objectMaxLength[j] = 20;
        } else {
          const l = value[jsonKeys[j]] ? (value[jsonKeys[j]].length + 3) : 0;
          objectMaxLength[j] =
            objectMaxLength[j] >= l
              ? objectMaxLength[j]
              : l;
        }
      }
      let key = jsonKeys;
      for (let j = 0; j < key.length; j++) {
        objectMaxLength[j] =
          objectMaxLength[j] >= key[j].length
            ? objectMaxLength[j]
            : key[j].length;
      }
    }

    const wscols = objectMaxLength.map(w => { return { width: w + 5 } });
    const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(json);
    worksheet["!cols"] = wscols;
    this.applyDateFormat(worksheet, isAllianz, excludeTimeColumns);
    const columnsToFormat = isAllianz
      ? ["Actual Trip Travel Distance (KM)", "First Onboard/Deboard Distance (KM)", "Total Distance (KM)"]
      : numericColumns;
    if (columnsToFormat.length) {
      this.applyNumericFormat(worksheet, columnsToFormat);
    }
    const workbook: XLSX.WorkBook = { Sheets: { 'data': worksheet }, SheetNames: ['data'] };
    const excelBuffer: any = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    this.saveAsExcelFile(excelBuffer, excelFileName);
  }

  public exportTripFeedbackReport(json: any[], excelFileName: string): void {
    const jsonKeys = Object.keys(json[0]);
    const objectMaxLength = [];
    for (let i = 0; i < json.length; i++) {
      const value = json[i];
      for (let j = 0; j < jsonKeys.length; j++) {
        const cellText = value[jsonKeys[j]] != null ? String(value[jsonKeys[j]]) : '';
        const length = cellText ? cellText.length + 3 : 0;
        objectMaxLength[j] = objectMaxLength[j] >= length ? objectMaxLength[j] : length;
      }
      for (let j = 0; j < jsonKeys.length; j++) {
        objectMaxLength[j] = objectMaxLength[j] >= jsonKeys[j].length ? objectMaxLength[j] : jsonKeys[j].length;
      }
    }

    const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(json);
    worksheet['!cols'] = objectMaxLength.map(w => ({ width: w + 5 }));
    const range = XLSX.utils.decode_range(worksheet['!ref']);
    for (let col = range.s.c; col <= range.e.c; col++) {
      const headerAddress = XLSX.utils.encode_cell({ r: 0, c: col });
      const headerCell = worksheet[headerAddress];
      if (headerCell && String(headerCell.v).toLowerCase() === 'date') {
        headerCell.v = 'Feedback Day';
        this.applyDateFormat(worksheet);
        headerCell.v = 'Date';
        break;
      }
    }
    const workbook: XLSX.WorkBook = { Sheets: { data: worksheet }, SheetNames: ['data'] };
    const excelBuffer: any = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    this.saveAsExcelFile(excelBuffer, excelFileName);
  }

  private saveAsExcelFile(buffer: any, fileName: string): void {
    const data: Blob = new Blob([buffer], {
      type: EXCEL_TYPE
    });
    FileSaver.saveAs(data, fileName + '_export_' + new Date().getTime() + EXCEL_EXTENSION);
  }

  /**
  * Common method to format all Date and Time cells
  */
  private applyDateFormat(
    worksheet: XLSX.WorkSheet,
    isAllianz = false,
    excludeTimeColumns: string[] = [],
  ): void {

    const range = XLSX.utils.decode_range(worksheet['!ref']);

    const dateColumnIndexes = [];
    const timeColumnIndexes = [];
    const dateTimeColumnIndexes = [];
    const excludedTimeHeaders = excludeTimeColumns.map(col => col.toLowerCase());

    //Find Date and Time columns from header
    for (let col = range.s.c; col <= range.e.c; col++) {

      const headerAddress = XLSX.utils.encode_cell({
        r: 0,
        c: col
      });

      const headerCell = worksheet[headerAddress];

      if (headerCell && typeof headerCell.v === 'string') {

        const header = headerCell.v.toLowerCase();

        if (header.includes('date') && header != 'lastupdatedby' && header != 'lastupdatedon' && !header.includes('datetime')) {
          dateColumnIndexes.push(col);
        }

        if ((header === 'tripstarttime' || header === 'tripstoptime') && isAllianz) {
          dateTimeColumnIndexes.push(col);
        }

        if (header.includes('datetime') || header === 'scheduledon' || header === 'lastupdatedon') {
          dateTimeColumnIndexes.push(col);
        }

        else if (header.includes('time') && !header.includes('trips') && !header.includes('%') && !excludedTimeHeaders.includes(header)) {
          timeColumnIndexes.push(col);
        }
      }
    }

    this.formatDateColumns(worksheet, range, dateColumnIndexes);
    this.formatTimeColumns(worksheet, range, timeColumnIndexes);
    if (isAllianz) {
      this.formatDateTimeColumns(worksheet, range, dateTimeColumnIndexes);
    } else {
      this.formatUpdatedOnAndCreatedOnColumns(worksheet, range, dateTimeColumnIndexes);
    }
  }


  private parseDateOnlyParts(
    value: any,
  ): { y: number; m: number; d: number } | null {
    if (value == null || value === "") {
      return null;
    }

    if (value instanceof Date) {
      return {
        y: value.getFullYear(),
        m: value.getMonth() + 1,
        d: value.getDate(),
      };
    }

    const str = String(value).trim().split(/[\sT]/)[0];
    const parts = str.split(/[-/]/);
    if (parts.length !== 3) {
      return null;
    }

    let y: number;
    let m: number;
    let d: number;

    if (parts[0].length === 4) {
      y = Number(parts[0]);
      m = Number(parts[1]);
      d = Number(parts[2]);
    } else if (parts[2].length === 4) {
      d = Number(parts[0]);
      m = Number(parts[1]);
      y = Number(parts[2]);
    } else {
      return null;
    }

    if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) {
      return null;
    }

    const test = new Date(y, m - 1, d);
    if (
      test.getFullYear() !== y ||
      test.getMonth() !== m - 1 ||
      test.getDate() !== d
    ) {
      return null;
    }

    return { y, m, d };
  }

  private toExcelDateSerial(y: number, m: number, d: number): number {
    const epoch = Date.UTC(1899, 11, 30);
    return (Date.UTC(y, m - 1, d) - epoch) / 86400000;
  }

  private formatDateColumns(
    worksheet: XLSX.WorkSheet,
    range: XLSX.Range,
    dateColumnIndexes: number[]
  ): void {
    for (let row = range.s.r + 1; row <= range.e.r; row++) {
      for (const col of dateColumnIndexes) {
        const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
        const cell = worksheet[cellAddress];

        if (!cell || cell.v === null || cell.v === undefined || cell.v === '' || cell.v === 'NA') {
          if (cell) {
            cell.v = 'N/A';
            cell.t = 's';
          }
          continue;
        }

        if (cell.v.toString().trim() === 'Total') {
          cell.v = 'Total';
          cell.t = 's';
          continue;
        }

        const parts = this.parseDateOnlyParts(cell.v);
        if (parts) {
          cell.v = this.toExcelDateSerial(parts.y, parts.m, parts.d);
          cell.t = 'n';
          cell.z = 'dd/mm/yyyy';
        } else {
          cell.v = "N/A";
          cell.t = 's';
        }
      }
    }
  }

  private formatUpdatedOnAndCreatedOnColumns(
    worksheet: XLSX.WorkSheet,
    range: XLSX.Range,
    dateColumnIndexes: number[]
  ): void {
    for (let row = range.s.r + 1; row <= range.e.r; row++) {
      for (const col of dateColumnIndexes) {
        const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
        const cell = worksheet[cellAddress];

        if (!cell || cell.v === null || cell.v === undefined || cell.v === '' || cell.v === 'N/A' || cell.v === 'NA') {
          if (cell) {
            cell.v = 'N/A';
            cell.t = 's';
          }
          continue;
        }

        if (cell.v.toString().trim() === 'Total') {
          cell.v = 'Total';
          cell.t = 's';
          continue;
        }

        let cellValue = cell.v;

        const value = cellValue.toString().replace('.0', '');
         const parts = value.split(' ');

      if (parts.length < 2) {
        cell.v = 'N/A';
        cell.t = 's';
        continue;
      }

      const datePart = parts[0];
      const timePart = parts[1];

      const dateParts = datePart.split('-').map(Number);

      let year: number;
      let month: number;
      let day: number;

      // yyyy-MM-dd
      if (dateParts[0] > 31) {
        [year, month, day] = dateParts;
      }
      // dd-MM-yyyy
      else {
        [day, month, year] = dateParts;
      }
        const [hour, minute, second] = timePart.split(':').map(Number);

        const date = new Date(year, month - 1, day, hour, minute, second);

        cell.v = date;
        cell.t = 'd';
        cell.z = 'dd/mm/yyyy hh:mm:ss';
      }
    }
  }


  private formatTimeColumns(
    worksheet: XLSX.WorkSheet,
    range: XLSX.Range,
    timeColumnIndexes: number[]
  ): void {

    for (let row = range.s.r + 1; row <= range.e.r; row++) {

      for (const col of timeColumnIndexes) {

        const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
        const cell = worksheet[cellAddress];

        if (!cell) continue;

        const rawV = cell.v?.toString().trim() || '';

        if (!rawV || ['NA', 'N/A'].includes(rawV.toUpperCase())) {
          cell.v = 'N/A';
          cell.t = 's';
          continue;
        }

        if (rawV.toLowerCase() === 'total') {
          cell.v = 'Total';
          cell.t = 's';
          continue;
        }

        try {

          if (typeof cell.v === 'number') {
            const totalSeconds = Math.round(cell.v * 86400);
            const hh = String(Math.floor(totalSeconds / 3600)).padStart(2, '0');
            const mm = String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, '0');

            cell.v = `${hh}:${mm}`;
            cell.t = 's';
            continue;
          }

          let formattedTime = rawV;

          // yyyy-MM-dd HH:mm:ss OR yyyy-MM-dd HH:mm
          if (rawV.includes(' ')) {
            const timePart = rawV.split(' ')[1];
            formattedTime = timePart ? timePart.substring(0, 5) : 'N/A';
          }

          // dd/MM/yyyy HH:mm:ss OR dd-MM-yyyy HH:mm:ss
          else {
            const match = rawV.match(
              /^\d{2}[\/-]\d{2}[\/-]\d{4}\s+(\d{1,2}:\d{2})/
            );
            if (match) {
              formattedTime = match[1];
            }
            // HH:mm:ss
            else if (/^\d{1,2}:\d{2}:\d{2}$/.test(rawV)) {
              formattedTime = rawV.substring(0, 5);
            }
            // HH:mm
            else if (/^\d{1,2}:\d{2}$/.test(rawV)) {
              formattedTime = rawV;
            }
          }

          cell.v = formattedTime;
          cell.t = 's';

        } catch {
          cell.v = 'N/A';
          cell.t = 's';
        }
      }
    }
  }

  private formatDateTimeColumns(
    worksheet: XLSX.WorkSheet,
    range: XLSX.Range,
    columnIndexes: number[]
  ): void {

    for (let row = range.s.r + 1; row <= range.e.r; row++) {

      for (const col of columnIndexes) {

        const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
        const cell = worksheet[cellAddress];

        if (!cell || !cell.v) {
          continue;
        }

        const value = cell.v.toString().trim();

        if (!value || value === 'NA' || value === 'N/A') {
          cell.v = 'N/A';
          cell.t = 's';
          continue;
        }

        const dateObj = new Date(value);

        if (!isNaN(dateObj.getTime())) {
          cell.v = dateObj;
          cell.t = 'd';
          cell.z = 'dd/mm/yyyy hh:mm';
        }
      }
    }
  }

  private applyNumericFormat(
    worksheet: XLSX.WorkSheet,
    columnNames: string[],
  ): void {
    if (!worksheet['!ref']) return;

    const range = XLSX.utils.decode_range(worksheet['!ref']);
    const columnIndexes: number[] = [];

    for (let col = range.s.c; col <= range.e.c; col++) {
      const headerAddress = XLSX.utils.encode_cell({ r: 0, c: col });
      const headerCell = worksheet[headerAddress];
      if (
        headerCell &&
        typeof headerCell.v === 'string' &&
        columnNames.includes(headerCell.v)
      ) {
        columnIndexes.push(col);
      }
    }

    for (let row = range.s.r + 1; row <= range.e.r; row++) {
      for (const col of columnIndexes) {
        const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
        const cell = worksheet[cellAddress];
        if (!cell) continue;

        const raw = cell.v;
        if (raw === 'NA' || raw === 'N/A') {
          cell.t = 's';
          cell.v = raw;
          continue;
        }

        const num = typeof raw === 'number' ? raw : Number(raw);
        if (Number.isFinite(num)) {
          cell.t = 'n';
          cell.v = Math.round(num * 100) / 100;
          cell.z = '0.00';
        }
      }
    }
  }

}